import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import {
  BrandAccountType,
  PLAN_ENTITLEMENTS,
  PlanLimit,
  ProgramStatus,
  upgradeTierFor,
} from '@fluenceur/types';
import CreateNewProgramModal from '@/components/actions/program/CreateNewProgramModal';
import ErrorBoundary from '@/components/ErrorBoundary';
import NewProgramButton from '@/components/program/browse/NewProgramButton';
import ProgramsStatusTabs from '@/components/program/browse/ProgramsStatusTabs';
import ProgramsGrid from '@/components/program/browse/ProgramsGrid';
import ProgramsErrorState from '@/components/program/browse/ProgramsErrorState';
import { browseColors } from '@/components/program/browse/browse-tokens';
import { useAffiliationAvailabilityGate } from '@/hooks/use-affiliation-availability';
import { useProgramLimitGate } from '@/hooks/use-program-limit-gate';
import { usePageTitleStore } from '@/stores/page-title';
import { PlanSuspendedBanner } from '@/components/subscription/plan-limit/PlanSuspendedBanner';
import { useAuth } from '@/contexts/auth-context';
import { useListProgram } from '@/hooks/queries/use-list-program';
import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';

// The scaffold's <main> provides no max-width or padding, so the page owns its
// own content container (validated design: 1320px, centered, 30/40/64 padding).
const STATUS_VALUES = [ProgramStatus.Active, ProgramStatus.Inactive] as ProgramStatus[];

const ProgramsBrowsePage = () => {
  const { t } = useTranslation();
  const setTitle = usePageTitleStore((state) => state.setTitle);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { user } = useAuth();
  const brandId = user?.currentBrandId;
  // The creation gate already reads this bulk key; observing it adds no per-program request.
  const programs = useListProgram(undefined, { enabled: !!brandId });
  const features = usePlanFeaturesQuery();
  const bypass =
    user?.currentBrand?.hasSubscriptionBypass ||
    user?.currentBrand?.accountType === BrandAccountType.INTERNAL ||
    user?.currentBrand?.accountType === BrandAccountType.PARTNER;
  const hasPausedPrograms =
    !!brandId &&
    !bypass &&
    programs.isSuccess &&
    !programs.isFetching &&
    features.isSuccess &&
    !features.isFetching &&
    PLAN_ENTITLEMENTS[features.planTier].maxActivePrograms !== null &&
    programs.data?.some(
      (program) => program.brandId === brandId && !program.enabled && !!program.pausedByPlanAt
    );

  const [status, setStatus] = useQueryState(
    'status',
    parseAsStringLiteral(STATUS_VALUES).withDefault(ProgramStatus.Active)
  );

  useEffect(() => {
    setTitle(t('programs.browse.heading'));
    return () => setTitle(null);
  }, [setTitle, t]);

  const guardProgramLimit = useProgramLimitGate();
  const availability = useAffiliationAvailabilityGate();
  const openCreate = () => availability.guard(() => guardProgramLimit(() => setIsCreateOpen(true)));

  return (
    <ErrorBoundary fallback={<ProgramsErrorState />}>
      <ProgramsStatusTabs status={status} onStatusChange={setStatus} />

      <Box maxW="1320px" mx="auto" pt="30px" pb="64px" px={{ base: 5, md: '40px' }}>
        <Flex as="header" mb="20px" gap={4} align="flex-start" justify="space-between">
          <Box>
            <Heading
              as="h1"
              fontSize="26px"
              fontWeight={600}
              letterSpacing="-0.02em"
              lineHeight="1.2"
              color={browseColors.heading}
            >
              {t('programs.browse.heading')}
            </Heading>
            <Text mt="5px" fontSize="13.5px" fontWeight={300} color={browseColors.subtitle}>
              {t('programs.browse.subtitle')}
            </Text>
          </Box>
          <NewProgramButton onClick={openCreate} flexShrink={0} />
        </Flex>

        {hasPausedPrograms && (
          <Box mb="20px">
            <PlanSuspendedBanner
              title={t('planSuspended.programs.title')}
              body={t('planSuspended.programs.body')}
              currentTier={features.planTier}
              targetTier={upgradeTierFor(PlanLimit.ACTIVE_PROGRAMS, features.planTier)}
            />
          </Box>
        )}
        <ProgramsGrid status={status} onCreate={openCreate} />
      </Box>

      {availability.modal}
      <CreateNewProgramModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </ErrorBoundary>
  );
};

export default ProgramsBrowsePage;
